import { useState, useEffect, useRef, useCallback } from "react";
import { useClock } from "@/hooks/useClock";
import { ChevronUp, StickyNote, Calendar, Sparkles } from "lucide-react";
import FsmLogo from "@/components/FsmLogo";

const NOTIF_ICONS = {
  Notes: <StickyNote size={16} className="text-yellow-300" />,
  Calendar: <Calendar size={16} className="text-red-300" />,
  corysuggs: <Sparkles size={16} className="text-sky-300" />,
  promo: <FsmLogo size={16} />,
};

export default function LockScreen({ content, onUnlock }) {
  const now = useClock();
  const [visibleCount, setVisibleCount] = useState(0);
  const [leaving, setLeaving] = useState(false);
  const startY = useRef(null);
  const notifs = content.lock_notifications || [];

  useEffect(() => {
    if (visibleCount >= notifs.length) return;
    const t = setTimeout(() => setVisibleCount((c) => c + 1), 900 + visibleCount * 900);
    return () => clearTimeout(t);
  }, [visibleCount, notifs.length]);

  const unlock = useCallback(() => {
    if (leaving) return;
    setLeaving(true);
    setTimeout(onUnlock, 650);
  }, [leaving, onUnlock]);

  const onTouchStart = (e) => (startY.current = e.touches[0].clientY);
  const onTouchEnd = (e) => {
    if (startY.current == null) return;
    const dy = startY.current - e.changedTouches[0].clientY;
    if (dy > 60) unlock();
    startY.current = null;
  };

  const timeStr = now.toLocaleTimeString(undefined, { hour: "numeric", minute: "2-digit" }).replace(/\s?[AP]M/i, "");
  const dateStr = now.toLocaleDateString(undefined, { weekday: "long", month: "long", day: "numeric" });

  return (
    <div
      data-testid="lock-screen"
      className="fixed inset-0 z-[200] overflow-hidden flex flex-col items-center select-none"
      onClick={unlock}
      onTouchStart={onTouchStart}
      onTouchEnd={onTouchEnd}
      style={{
        backgroundImage: content.lock_photo_url
          ? `url(${process.env.REACT_APP_BACKEND_URL}${content.lock_photo_url})`
          : "url(https://images.unsplash.com/photo-1506905925346-21bda4d32df4?q=80&w=1920&auto=format&fit=crop)",
        backgroundSize: "cover",
        backgroundPosition: "center",
        transform: leaving ? "translateY(-100%)" : "translateY(0)",
        transition: "transform 650ms cubic-bezier(0.7,0,0.3,1)",
      }}
    >
      <div className="absolute inset-0 bg-black/25" />
      <div className="relative z-10 mt-[12vh] text-center text-white">
        <div className="text-lg font-medium opacity-90" data-testid="lock-date">{dateStr}</div>
        <div className="text-[84px] md:text-[110px] font-bold leading-none tracking-tight" data-testid="lock-time">
          {timeStr}
        </div>
        <div className="mt-1 text-base italic opacity-90" data-testid="lock-tagline">{content.tagline}</div>
      </div>

      <div className="relative z-10 mt-6 w-full max-w-sm px-4 flex flex-col gap-2">
        {notifs.slice(0, visibleCount).map((n, i) => {
          const isPromo = n.id === "promo";
          const inner = (
            <>
              <div className={`w-8 h-8 rounded-lg flex items-center justify-center shrink-0 ${isPromo ? "bg-black/80" : "bg-white/30"}`}>
                {NOTIF_ICONS[n.id] || NOTIF_ICONS[n.app] || <Sparkles size={16} className="text-white" />}
              </div>
              <div className="min-w-0">
                {!isPromo && (
                  <div className="flex items-baseline gap-2">
                    <span className="text-[13px] font-semibold">{n.app}</span>
                    <span className="text-[11px] opacity-70">now</span>
                  </div>
                )}
                <div className="text-[13px] font-medium">{n.title}</div>
                <div className="text-[12px] opacity-85 truncate">{n.body}</div>
                {n.footnote && <div className="text-[11px] opacity-60 italic mt-0.5">{n.footnote}</div>}
              </div>
            </>
          );
          const cls = "flex items-start gap-3 p-3 rounded-2xl bg-white/25 border border-white/25 text-white w-full text-left";
          const style = {
            backdropFilter: "blur(20px)",
            animation: "lockNotifIn 500ms cubic-bezier(0.2,1.2,0.4,1) both",
          };
          return n.url ? (
            <a key={n.id || i} href={n.url} target="_blank" rel="noreferrer"
              data-testid={`lock-notification-${i}`} className={cls} style={style}
              onClick={(e) => e.stopPropagation()}>
              {inner}
            </a>
          ) : (
            <div key={n.id || i} data-testid={`lock-notification-${i}`} className={cls} style={style}>
              {inner}
            </div>
          );
        })}
      </div>

      <div className="relative z-10 mt-auto mb-8 pb-[env(safe-area-inset-bottom)] flex flex-col items-center gap-1 text-white/90">
        <ChevronUp size={20} className="animate-bounce" />
        <span className="text-sm font-medium" data-testid="lock-swipe-hint">Swipe up to unlock</span>
      </div>

      <style>{`@keyframes lockNotifIn { from { opacity: 0; transform: translateY(-14px) scale(0.96); } to { opacity: 1; transform: translateY(0) scale(1); } }`}</style>
    </div>
  );
}
